import { AntdTabsProps, useHelmet } from '@agro-club/agroclub-shared'
import { endpoints } from 'modules/endpoints'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { AAntdTabs } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import { WrapperContent } from 'views/layouts/NewLayout'
import { AverageTable } from 'views/pages/LogisticsKR/Rates/components/RatesTables/AverageTable'
import { CommonRatesTable } from 'views/pages/LogisticsKR/Rates/components/RatesTables/CommonRatesTable'
import { AnalyticsPages, AnalyticsPlaces } from '../analyticsPlaces'
import { prepareMultipleEditing } from '../helpers'
import { AddRatesModal } from './components/AddRatesModal'
import { EditRoadRatesModal } from './components/EditRoadRatesModal'
import { RatesListHeader } from './components/RatesListContent/RatesListHeader'
import RatesRoutes from './routes'
import { EditableRatesDTO, RateDTO, RatesTabTypes, tabProductMap } from './types'
import { useAverageColumns } from './useAverageColumns'
import { useColumns } from './useColumns'
import { Tab, useTab, useTabsDataV2 } from 'hooks/useTabsDataV2'
import { RailsRatesTable } from './components/RailsRates/RailsRatesTable'
import { AddRailsRatesModal } from './components/RailsRates/AddRailsRatesModal'
import { roadsInitialParams } from './components/RatesFilters'

const useRoadsItem = (key: RatesTabTypes, label, tab: Tab, setSelectedRows, onSuccess, isFiltersLoaded) => {
  return {
    key,
    label: label,
    children: useMemo(
      () => (
        <CommonRatesTable
          listRequestParams={tab.params}
          isFiltersLoaded={isFiltersLoaded}
          setListRequestParams={tab.setter}
          dataEndpoint={tab.endpoint ?? ''}
          columnsFunc={useColumns}
          setSelectedRows={setSelectedRows}
          onSuccess={onSuccess}
        />
      ),
      [tab.params, tab.setter, tab.endpoint, isFiltersLoaded, setSelectedRows, onSuccess],
    ),
  }
}

const roadsFilterList = ['logisticians', 'country', 'region']
const railsFilterList = ['search', 'product']

const useAverageItem = (key: RatesTabTypes, label, tab: Tab) => {
  return {
    key,
    label: label,
    children: useMemo(
      () => (
        <AverageTable
          listRequestParams={tab.params}
          setListRequestParams={tab.setter}
          // '' for typecheck
          dataEndpoint={tab.endpoint ?? ''}
          columnsFunc={useAverageColumns}
        />
      ),
      [tab.params, tab.setter, tab.endpoint],
    ),
  }
}

const useRailsItem = (key: RatesTabTypes, label, tab: Tab, onSuccess, isFiltersLoaded) => {
  return {
    key,
    label: label,
    children: useMemo(
      () => (
        <RailsRatesTable
          listRequestParams={tab.params}
          isFiltersLoaded={isFiltersLoaded}
          setListRequestParams={tab.setter}
          dataEndpoint={tab.endpoint ?? ''}
          onSuccess={onSuccess}
        />
      ),
      [tab.params, tab.setter, tab.endpoint, isFiltersLoaded, onSuccess],
    ),
  }
}

const RatesList = () => {
  const { t } = useTranslation('rates')
  useHelmet({ title: t('metaTitle') })

  const navigate = useNavigate()
  const { activeTab: activeTabParams } = useParams()
  const [activeTab, setActiveTab] = useState<string>(activeTabParams || RatesTabTypes.cereals)
  const [selectedRows, setSelectedRows] = useState<RateDTO[]>([])
  const [isAddRoadsOpen, setIsAddRoadsOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isAddRailsOpen, setIsAddRailsOpen] = useState<boolean>(false)
  const rowsPreparedForEditing = prepareMultipleEditing(selectedRows)
  const isEditingAvailable = selectedRows.length > 0

  const tabs: Record<string, Tab> = {}
  tabs[RatesTabTypes.cereals] = useTab({
    endpoint: `${endpoints.LogisticsKR.ratesWithRegion()}?product_type=cereals`,
    label: t('productTypes.cereals'),
    //nonEditableParams: getNonEditableFilter(RatesTabTypes.cereals),
    initialParams: roadsInitialParams,
  })
  tabs[RatesTabTypes.oilseeds] = useTab({
    label: t('productTypes.oilseeds'),
    endpoint: `${endpoints.LogisticsKR.ratesWithRegion()}?product_type=oilseeds`,
    //nonEditableParams: getNonEditableFilter(RatesTabTypes.oilseeds),
    initialParams: roadsInitialParams,
  })
  tabs[RatesTabTypes.average] = useTab({
    label: t('productTypes.average'),
    endpoint: endpoints.LogisticsKR.ratesWithoutRegionAndProductType(),
  })
  tabs[RatesTabTypes.cerealsRails] = useTab({
    endpoint: `${endpoints.LogisticsKR.railwaysRates()}?product_type=cereals`,
    label: t('productTypes.cerealsRails'),
    //nonEditableParams: getNonEditableFilter(RatesTabTypes.cerealsRails),
  })
  tabs[RatesTabTypes.oilseedsRails] = useTab({
    label: t('productTypes.oilseedsRails'),
    endpoint: `${endpoints.LogisticsKR.railwaysRates()}?product_type=oilseeds`,
    //nonEditableParams: getNonEditableFilter(RatesTabTypes.oilseedsRails),
  })

  const { isFiltersLoaded, commonRequestParams, updateCommonFilterState, clearAllTabsState } = useTabsDataV2({
    activeTab,
    tabs,
    excludeFilters: {
      [RatesTabTypes.cerealsRails]: roadsFilterList,
      [RatesTabTypes.oilseedsRails]: roadsFilterList,
      [RatesTabTypes.cereals]: railsFilterList,
      [RatesTabTypes.oilseeds]: railsFilterList,
      [RatesTabTypes.average]: [...railsFilterList, ...roadsFilterList],
    },
  })

  const updateSelectedRowsAfterEdit = useCallback(
    (data: EditableRatesDTO) => {
      if (!data) return
      const selectedRowsDto = selectedRows.map((row) => ({
        ...row,
        logistics_rates: data.rates.map(({ rate }, i) => ({
          ...data.rates[i],
          rate: rate,
        })),
      }))

      setSelectedRows(selectedRowsDto)
    },
    [selectedRows],
  )

  const onSuccess = useCallback(() => {
    updateCommonFilterState(commonRequestParams.filter)
  }, [commonRequestParams, updateCommonFilterState])

  useEffect(() => {
    navigate(
      {
        pathname: generatePath(RatesRoutes.ListWithTab, { activeTab: activeTab }),
        search: location.search,
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const tabItems: AntdTabsProps['items'] = [
    useRoadsItem(
      RatesTabTypes.cereals,
      tabs[RatesTabTypes.cereals].label,
      tabs[RatesTabTypes.cereals],
      setSelectedRows,
      onSuccess,
      isFiltersLoaded,
    ),
    useRoadsItem(
      RatesTabTypes.oilseeds,
      tabs[RatesTabTypes.oilseeds].label,
      tabs[RatesTabTypes.oilseeds],
      setSelectedRows,
      onSuccess,
      isFiltersLoaded,
    ),
    useAverageItem(RatesTabTypes.average, tabs[RatesTabTypes.average].label, tabs[RatesTabTypes.average]),
    useRailsItem(
      RatesTabTypes.cerealsRails,
      tabs[RatesTabTypes.cerealsRails].label,
      tabs[RatesTabTypes.cerealsRails],
      onSuccess,
      isFiltersLoaded,
    ),
    useRailsItem(
      RatesTabTypes.oilseedsRails,
      tabs[RatesTabTypes.oilseedsRails].label,
      tabs[RatesTabTypes.oilseedsRails],
      onSuccess,
      isFiltersLoaded,
    ),
  ]

  return (
    <APageWrapper page={AnalyticsPages.RATES} place={AnalyticsPlaces.RATES.LIST}>
      <WrapperContent>
        <RatesListHeader
          listRequestParams={tabs[activeTab].params}
          updateFilterState={updateCommonFilterState}
          clearAllTabsState={clearAllTabsState}
          setIsAddRoadsOpen={setIsAddRoadsOpen}
          setIsAddRailsOpen={setIsAddRailsOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          isEditingAvailable={isEditingAvailable}
        />

        <AAntdTabs items={tabItems} onChange={setActiveTab} activeKey={activeTab} id="tabs" />

        {isAddRoadsOpen && (
          <AddRatesModal
            refetch={onSuccess}
            onClose={() => setIsAddRoadsOpen(false)}
            currentProductType={tabProductMap[activeTab]}
          />
        )}

        {isAddRailsOpen && (
          <AddRailsRatesModal
            refetch={onSuccess}
            onClose={() => setIsAddRailsOpen(false)}
            currentProductType={tabProductMap[activeTab]}
          />
        )}

        {isEditModalOpen && (
          <EditRoadRatesModal
            refetch={onSuccess}
            onClose={() => setIsEditModalOpen(false)}
            currentProductType={tabProductMap[activeTab]}
            regionIds={rowsPreparedForEditing?.id}
            ratesData={rowsPreparedForEditing?.logistic_rates}
            responsibleLogisticians={rowsPreparedForEditing?.responsible_logisticians}
            updateSelectedRows={updateSelectedRowsAfterEdit}
          />
        )}
      </WrapperContent>
    </APageWrapper>
  )
}

export default RatesList
